import { useEffect, useState } from "react";
import { GetCurrentRelease, GetReleases, GetDeployments } from "../services/ShadowPulseService";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';

interface CurrentRelease {
    release_name: string;
    release_page: string;
    started_at: number;
}

interface Release {
    release_name: string;
    release_page: string;
    started_at: number;
    ended_at: number | null;
}

interface Deployment {
    stack_id: string;
    stack_ref: string;
    version: string;
    current_epoch: number;
    current_release: string;
}

const DeploymentsPage = () => {
    const [release, setRelease] = useState<CurrentRelease | null>(null);
    const [releases, setReleases] = useState<Release[]>([]);
    const [deployments, setDeployments] = useState<Deployment[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        Promise.all([
            GetCurrentRelease(""),
            GetReleases(""),
            GetDeployments("")
        ]).then(([currentData, releasesData, deploymentsData]) => {
            setRelease(currentData);
            setReleases(releasesData.releases.slice(0, 3));
            setDeployments(deploymentsData.deployments);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, []);

    const formatDate = (epoch: number) => {
        return new Date(epoch * 1000).toLocaleString();
    };

    const deployedAtTemplate = (rowData: Deployment) => {
        return formatDate(rowData.current_epoch);
    };

    return (
        <div className="flex flex-column py-4">
            <div className="ml-3">
                <h2>Deployments</h2>
            </div>
            
            {loading ? (
                <div className="ml-3 mt-3">Loading...</div>
            ) : (
                <>
                    {release && (
                        <div className="card p-4 shadow-2 m-3">
                            <h3 className="text-lg font-medium mb-3">Current Release</h3>
                            <div className="flex flex-column gap-2">
                                <div><strong>Release:</strong> {release.release_name}</div>
                                <div><strong>Started:</strong> {formatDate(release.started_at)}</div>
                                <div>
                                    <strong>Release Page:</strong>{" "}
                                    <a href={release.release_page} target="_blank" rel="noopener noreferrer" className="text-primary">
                                        {release.release_page}
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}
                    
                    {releases.length > 0 && (
                        <div className="card p-4 shadow-2 m-3">
                            <h3 className="text-lg font-medium mb-3">Recent Releases</h3>
                            <DataTable value={releases} stripedRows>
                                <Column field="release_name" header="Release" />
                                <Column header="Started" body={(rowData: Release) => formatDate(rowData.started_at)} />
                                <Column header="Ended" body={(rowData: Release) => rowData.ended_at ? formatDate(rowData.ended_at) : '-'} />
                                <Column header="Release Page" body={(rowData: Release) => (
                                    <a href={rowData.release_page} target="_blank" rel="noopener noreferrer" className="text-primary">
                                        View
                                    </a>
                                )} />
                            </DataTable>
                        </div>
                    )}
                    
                    {deployments.length > 0 && (
                        <div className="card p-4 shadow-2 m-3">
                            <h3 className="text-lg font-medium mb-3">Deployments</h3>
                            <DataTable value={deployments} stripedRows>
                                <Column field="stack_id" header="Stack ID" />
                                <Column field="stack_ref" header="Stack Ref" />
                                <Column field="version" header="Version" />
                                <Column field="current_release" header="Current Release" />
                                <Column header="Deployed At" body={deployedAtTemplate} />
                            </DataTable>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default DeploymentsPage;
